import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentSession } from "../../services/auth";

const API_URL = (
  process.env.REACT_APP_API_URL || "https://zedro-backend.onrender.com"
).replace(/\/$/, "");

function slugify(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 255);
}

export default function ProductCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [productType, setProductType] = useState("program");
  const [visibility, setVisibility] = useState("public");
  const [status, setStatus] = useState("draft");
  const [stripeProductId, setStripeProductId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const effectiveSlug = useMemo(
    () => (slugEdited ? slug : slugify(name)),
    [name, slug, slugEdited]
  );

  async function submit(event) {
    event.preventDefault();
    setError("");

    const normalizedName = name.trim();
    const normalizedSlug = effectiveSlug.trim();
    if (!normalizedName || !normalizedSlug) {
      setError("Nombre y slug son obligatorios.");
      return;
    }

    try {
      setSaving(true);
      const session = getCurrentSession();
      const response = await fetch(`${API_URL}/api/admin/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {}),
        },
        body: JSON.stringify({
          name: normalizedName,
          slug: normalizedSlug,
          description: description.trim() || null,
          product_type: productType,
          visibility,
          status,
          stripe_product_id: stripeProductId.trim() || null,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.product?.id) {
        throw new Error(data?.error || "No fue posible crear el producto.");
      }

      navigate(`/admin/productos/${data.product.id}`);
    } catch (err) {
      console.error("[ADMIN_PRODUCT_CREATE]", err);
      setError(err.message || "No fue posible crear el producto.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main style={pageStyle}>
      <div style={topBarStyle}>
        <div>
          <div style={eyebrowStyle}>Oficina Central · Productos</div>
          <h1 style={titleStyle}>Crear producto</h1>
          <p style={descriptionStyle}>
            Define la identidad del producto. La oferta comercial y sus precios se
            configuran después desde su ficha, sin mezclar la naturaleza del producto
            con la forma en que se vende.
          </p>
        </div>
        <Link to="/admin/productos" style={backLinkStyle}>← Volver a Productos</Link>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <form onSubmit={submit} style={cardStyle}>
        <div style={gridStyle}>
          <Field label="Nombre" wide>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              style={inputStyle}
            />
          </Field>

          <Field label="Slug" wide>
            <input
              value={effectiveSlug}
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(slugify(event.target.value));
              }}
              required
              style={inputStyle}
            />
          </Field>

          <Field label="Tipo">
            <select value={productType} onChange={(event) => setProductType(event.target.value)} style={inputStyle}>
              <option value="program">Programa formativo</option>
              <option value="membership">Membresía</option>
              <option value="asset">Recurso / asset</option>
              <option value="tool">Herramienta</option>
            </select>
          </Field>

          <Field label="Visibilidad">
            <select value={visibility} onChange={(event) => setVisibility(event.target.value)} style={inputStyle}>
              <option value="public">Público</option>
              <option value="member">Miembros</option>
            </select>
          </Field>

          <Field label="Estado">
            <select value={status} onChange={(event) => setStatus(event.target.value)} style={inputStyle}>
              <option value="draft">Borrador</option>
              <option value="published">Publicado</option>
              <option value="archived">Archivado</option>
            </select>
          </Field>

          <Field label="Stripe Product ID">
            <input
              value={stripeProductId}
              onChange={(event) => setStripeProductId(event.target.value)}
              placeholder="prod_... (opcional)"
              style={inputStyle}
            />
          </Field>

          <Field label="Descripción" wide>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={5}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </Field>
        </div>

        <div style={footerStyle}>
          <div style={noteStyle}>
            Recomendación operativa: crea primero el producto como borrador, configura
            su precio y asociaciones y publícalo únicamente cuando el circuito esté completo.
          </div>
          <button type="submit" disabled={saving} style={buttonStyle}>
            {saving ? "Creando..." : "Crear producto"}
          </button>
        </div>
      </form>
    </main>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <label style={{ ...fieldStyle, ...(wide ? wideStyle : {}) }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

const pageStyle = {
  maxWidth: 920,
  margin: "2rem auto",
  padding: "0 1rem 3rem",
  fontFamily: "Questrial, sans-serif",
};
const topBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 18,
  alignItems: "flex-start",
  flexWrap: "wrap",
  marginBottom: 20,
};
const eyebrowStyle = {
  color: "#9a7634",
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: ".1em",
  textTransform: "uppercase",
};
const titleStyle = { margin: "6px 0 0", color: "#0a2230", fontSize: 30 };
const descriptionStyle = {
  maxWidth: 680,
  margin: "8px 0 0",
  color: "#64748b",
  fontSize: 14,
  lineHeight: 1.6,
};
const backLinkStyle = {
  textDecoration: "none",
  color: "#264763",
  fontWeight: 800,
  fontSize: 13,
};
const cardStyle = {
  padding: 20,
  border: "1px solid #e6ebf1",
  borderRadius: 16,
  background: "#fff",
  boxShadow: "0 14px 36px rgba(15,47,70,.06)",
};
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: 14,
};
const fieldStyle = { display: "grid", gap: 6 };
const wideStyle = { gridColumn: "1 / -1" };
const labelStyle = { color: "#405064", fontSize: 12, fontWeight: 800 };
const inputStyle = {
  width: "100%",
  minHeight: 42,
  boxSizing: "border-box",
  border: "1px solid #d7dee8",
  borderRadius: 9,
  background: "#fff",
  padding: "9px 10px",
  color: "#213348",
  font: "inherit",
};
const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 16,
  flexWrap: "wrap",
  marginTop: 20,
  paddingTop: 16,
  borderTop: "1px solid #eef2f7",
};
const noteStyle = { maxWidth: 620, color: "#7b8797", fontSize: 12, lineHeight: 1.55 };
const buttonStyle = {
  minHeight: 42,
  padding: "0 17px",
  border: 0,
  borderRadius: 10,
  background: "#264763",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};
const errorStyle = {
  marginBottom: 14,
  padding: "11px 13px",
  borderRadius: 10,
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#b42318",
  fontSize: 13,
};
